import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	CadRendererBroadcastManager,
	CadRendererSnapshotEvent,
	createDisabledCadRendererSyncStatus,
} from './broadcast-manager.js';
import { createRequestId, parseSnapshotRequest } from './sync-protocol.js';

import type {
	CadRendererBroadcastManagerOptions,
	CadRendererPublishEvent,
	CadRendererSyncEnvelope,
	CadRendererSyncNamespace,
	CadRendererSyncScope,
	CadRendererSyncStatus,
} from './broadcast-manager.js';

export type CadRendererSyncCore = {
	status: CadRendererSyncStatus;
	windowId: string;
	publish: <TPayload extends object>(
		namespace: Exclude<CadRendererSyncNamespace, CadRendererSyncNamespace.Presence>,
		event: CadRendererPublishEvent,
		payload: TPayload,
	) => void;
	subscribe: (
		namespace: Exclude<CadRendererSyncNamespace, CadRendererSyncNamespace.Presence>,
		listener: (envelope: CadRendererSyncEnvelope) => void,
	) => () => void;
};

export type UseCadRendererSyncCoreOptions = {
	syncScope?: CadRendererSyncScope;
	broadcastOptions?: Pick<CadRendererBroadcastManagerOptions, 'channelFactory' | 'now' | 'windowId'>;
};

export type SnapshotPayload = {
	requestId?: string;
};

/**
 * Owns the BroadcastChannel manager lifecycle and exposes typed publish/subscribe helpers for sync features.
 */
export function useCadRendererSyncCore({
	syncScope,
	broadcastOptions,
}: UseCadRendererSyncCoreOptions): CadRendererSyncCore {
	const syncProjectId = syncScope?.projectId;
	const syncRevisionId = syncScope?.revisionId;
	const [manager, setManager] = useState<CadRendererBroadcastManager | null>(null);
	const [status, setStatus] = useState<CadRendererSyncStatus>(() =>
		createDisabledCadRendererSyncStatus(Boolean(syncScope)),
	);

	/* eslint-disable react-hooks/set-state-in-effect -- This effect owns the BroadcastChannel lifecycle; state mirrors an external channel instance. */
	useEffect(() => {
		if (!syncProjectId || !syncRevisionId) {
			setManager(null);
			setStatus(createDisabledCadRendererSyncStatus(false));
			return;
		}

		const nextManager = new CadRendererBroadcastManager(
			{ projectId: syncProjectId, revisionId: syncRevisionId },
			broadcastOptions,
		);
		setManager(nextManager);
		return () => {
			nextManager.close();
			setManager((current) => (current === nextManager ? null : current));
		};
	}, [broadcastOptions, syncProjectId, syncRevisionId]);

	useEffect(() => {
		if (!manager) {
			setStatus(createDisabledCadRendererSyncStatus(Boolean(syncProjectId && syncRevisionId)));
			return;
		}

		return manager.subscribeStatus(setStatus);
	}, [manager, syncProjectId, syncRevisionId]);
	/* eslint-enable react-hooks/set-state-in-effect */

	const publish = useCallback<CadRendererSyncCore['publish']>(
		(namespace, event, payload) => {
			manager?.publish(namespace, event, payload);
		},
		[manager],
	);

	const subscribe = useCallback<CadRendererSyncCore['subscribe']>(
		(namespace, listener) => {
			if (!manager) {
				return () => undefined;
			}

			return manager.subscribe((envelope) => {
				if (envelope.namespace === namespace) {
					listener(envelope);
				}
			});
		},
		[manager],
	);

	return useMemo(
		() => ({
			status,
			windowId: manager?.windowId ?? 'local',
			publish,
			subscribe,
		}),
		[manager?.windowId, publish, status, subscribe],
	);
}

/**
 * Responds to valid snapshot requests in one sync namespace with the caller's current feature payload.
 */
export function useSyncSnapshotResponder<TPayload extends SnapshotPayload>({
	core,
	namespace,
	createPayload,
}: {
	core: CadRendererSyncCore;
	namespace: Exclude<CadRendererSyncNamespace, CadRendererSyncNamespace.Presence>;
	createPayload: () => TPayload;
}) {
	const { publish, subscribe } = core;

	useEffect(() => {
		return subscribe(namespace, (envelope) => {
			if (envelope.event !== CadRendererSnapshotEvent.SnapshotRequest) {
				return;
			}

			const request = parseSnapshotRequest(envelope.payload);
			if (!request) {
				return;
			}

			publish(namespace, CadRendererSnapshotEvent.SnapshotResponse, {
				...createPayload(),
				requestId: request.requestId,
			});
		});
	}, [createPayload, namespace, publish, subscribe]);
}

/**
 * Requests peer snapshots for one namespace and applies matching responses after a short collection window.
 */
export function useSyncSnapshotHydration<TPayload extends SnapshotPayload>({
	core,
	namespace,
	parsePayload,
	applyResponses,
	responseWindowMs = 250,
}: {
	core: CadRendererSyncCore;
	namespace: Exclude<CadRendererSyncNamespace, CadRendererSyncNamespace.Presence>;
	parsePayload: (value: unknown) => TPayload | null;
	applyResponses: (responses: readonly TPayload[]) => void;
	responseWindowMs?: number;
}) {
	const { publish, status, subscribe } = core;

	useEffect(() => {
		if (!status.isEnabled) {
			return;
		}

		const requestId = createRequestId(namespace);
		const responses: TPayload[] = [];
		const unsubscribe = subscribe(namespace, (envelope) => {
			if (envelope.event !== CadRendererSnapshotEvent.SnapshotResponse) {
				return;
			}

			const payload = parsePayload(envelope.payload);
			if (payload?.requestId === requestId) {
				responses.push(payload);
			}
		});

		publish(namespace, CadRendererSnapshotEvent.SnapshotRequest, { requestId });
		const timer = setTimeout(() => {
			unsubscribe();
			applyResponses(responses);
		}, responseWindowMs);

		return () => {
			unsubscribe();
			clearTimeout(timer);
		};
	}, [applyResponses, namespace, parsePayload, publish, responseWindowMs, status.isEnabled, subscribe]);
}
